import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { currentPayformSelector } from '@app/state/payform';
import { Store } from '@ngrx/store';
import { PayformStatusCd } from '@xpo-ltl/sdk-common';
import { DsrPayform } from '@xpo-ltl/sdk-linehaulpayform';
import * as _ from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { ErrorWarning } from '../../../../classes/error-warning';
import { ErrorWarningMessage } from '../../../../classes/error-warning-message';
import { ErrorWarningTypes } from '../../../../enums';
import { ErrorMessage } from '../../../../enums/error-message.enum';
import { WarningMessage } from '../../../../enums/warning-message.enum';

@Injectable()
export class ErrorWarningService {
  private PayformWarningSubject = new BehaviorSubject(undefined);
  public PayformWarning$ = this.PayformWarningSubject.asObservable();

  private DetailWarningSubject = new BehaviorSubject(undefined);
  public DetailWarning$ = this.DetailWarningSubject.asObservable();

  private ActivityWarningSubject = new BehaviorSubject(undefined);
  public ActivityWarning$ = this.ActivityWarningSubject.asObservable();

  constructor(private store: Store) {}

  public payformWarningsChanged() {
    this.PayformWarningSubject.next(true);
  }
  public detailWarningsChanged(schSequenceNbr: string) {
    this.DetailWarningSubject.next(schSequenceNbr);
  }
  public activityWarningsChanged(schSequenceNbr: string) {
    this.ActivityWarningSubject.next(schSequenceNbr);
  }

  public collectWarnings(control: AbstractControl, searchRecursively: boolean = true): Object[] {
    let warningsObj = [];

    const findControlWarnings = formControl => {
      if (formControl && formControl.enabled && formControl['warnings'] && _.keys(formControl['warnings']).length) {
        warningsObj = [...warningsObj, ..._.values(formControl['warnings'])];
      }
      if (searchRecursively) {
        _.forEach(_.get(formControl, 'controls', []), c => findControlWarnings(c));
      }
    };

    findControlWarnings(control);

    return this.combineMessages(warningsObj, ErrorWarningTypes.Warning);
  }

  public collectErrors(control: AbstractControl, searchRecursively: boolean = true): Object[] {
    const errorsObj = [];

    const findControlErrors = formControl => {
      if (_.size(formControl.errors)) {
        const displayErrors = _.filter(formControl.errors, error => !!error.displayError);
        errorsObj.push(...displayErrors);
      }
      if (searchRecursively) {
        _.forEach(_.get(formControl, 'controls', []), c => findControlErrors(c));
      }
    };

    findControlErrors(control);

    return this.combineMessages(errorsObj, ErrorWarningTypes.Error);
  }

  private combineMessages(errorWarnings: ErrorWarning[], errorWarningType: string) {
    if (!errorWarnings.length) {
      return errorWarnings;
    }

    const returnErrorWarnings = [];
    const dupErrorWarnings = [];
    const errorWarningKeyMap = this.mapErrorWarningKeys(errorWarnings);

    _.forEach(errorWarnings, errorWarning => {
      errorWarningKeyMap[errorWarning.type] === 1 ? returnErrorWarnings.push(this.constructErrorWarningMessage(errorWarning, true, errorWarningType)) : dupErrorWarnings.push(errorWarning);
    });

    if (_.every(errorWarningKeyMap, key => key === 1)) {
      return returnErrorWarnings;
    }

    const consolodatedDupErrorWarnings = {};

    _.forEach(dupErrorWarnings, dupErrorWarning => {
      consolodatedDupErrorWarnings[dupErrorWarning.type]
        ? consolodatedDupErrorWarnings[dupErrorWarning.type].dupErrorWarnings.push(dupErrorWarning)
        : (consolodatedDupErrorWarnings[dupErrorWarning.type] = {
            type: dupErrorWarning.type,
            primarySubjects: [],
            secondarySubjects: [],
            dupErrorWarnings: [dupErrorWarning],
          });
    });

    _.forEach(consolodatedDupErrorWarnings, consDupErrorWarning => {
      _.forEach(consDupErrorWarning.dupErrorWarnings, dupErrorWarning => {
        consDupErrorWarning.primarySubjects.push(dupErrorWarning.primarySubject);
        consDupErrorWarning.secondarySubjects.push(dupErrorWarning.secondarySubject);
      });

      const type = consDupErrorWarning.type;
      const primarySubject = this.buildListSentenceString(consDupErrorWarning.primarySubjects);
      let secondarySubject;
      if (_.some(consDupErrorWarning.secondarySubjects, subject => subject === 'totalCount')) {
        secondarySubject = consDupErrorWarning.primarySubjects.length;
      } else {
        secondarySubject = this.buildListSentenceString(consDupErrorWarning.secondarySubjects);
      }
      const combinedErrorWarningObj = { type, primarySubject, secondarySubject };
      const combinedErrorWarning = this.constructErrorWarningMessage(combinedErrorWarningObj, false, errorWarningType);

      returnErrorWarnings.push(combinedErrorWarning);
    });

    return returnErrorWarnings;
  }

  private buildListSentenceString(list): string {
    return `${list.slice(0, list.length - 1).join(', ')} and ${list.slice(-1)}`;
  }

  private mapErrorWarningKeys(errorWarnings): object {
    const keyMap = {};

    _.forEach(errorWarnings, errorWarning => {
      keyMap[errorWarning.type] = keyMap[errorWarning.type] ? keyMap[errorWarning.type] + 1 : 1;
    });

    return keyMap;
  }

  private constructErrorWarningMessage({ type, primarySubject, secondarySubject }, isUnique: boolean = true, errorWarningType: string): ErrorWarningMessage {
    const typeCaps = type[0].toUpperCase() + type.slice(1);
    let baseMessage;

    if (errorWarningType === ErrorWarningTypes.Error) {
      baseMessage = isUnique ? ErrorMessage[typeCaps] : ErrorMessage[`${typeCaps}s`];
    } else if (errorWarningType === ErrorWarningTypes.Warning) {
      baseMessage = isUnique ? WarningMessage[typeCaps] : WarningMessage[`${typeCaps}s`];
    }

    const message = baseMessage.replace('{0}', primarySubject).replace('{1}', secondarySubject);
    const errorWarningMessage = new ErrorWarningMessage(type, message);

    return errorWarningMessage;
  }

  public shouldDisplayWarnings(): boolean {
    let currentPayform: DsrPayform;
    this.store.select(currentPayformSelector).subscribe(payform => {
      currentPayform = payform;
    });
    return (
      currentPayform.statusCd === PayformStatusCd.APPROVED ||
      currentPayform.statusCd === PayformStatusCd.RETURNED ||
      currentPayform.statusCd === PayformStatusCd.NEW ||
      currentPayform.statusCd === PayformStatusCd.SUBMITTED ||
      currentPayform.statusCd === PayformStatusCd.TRANSMITTED
    );
  }

  public hasWarning(warningsCollection: Array<Object>, warning: string): boolean {
    return !!_.find(warningsCollection, warningObj => warningObj.type === warning);
  }
}
